import { useCallback, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, X } from "lucide-react";
import { isDocumentStillProcessing } from "@/domains/documents/utils/documentState";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import type { LinkedDocument } from "@/domains/pm/types";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { useOpenLinkedDoc } from "@/domains/pm/hooks/useOpenLinkedDoc";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { PanelSectionLabel } from "./PanelSectionLabel";
import { IssueAttachMenu } from "./IssueAttachMenu";
import { IssueDocumentPicker } from "./IssueDocumentPicker";
import { LinkedDocumentRow } from "./LinkedDocumentRow";
import { AttachmentGallery, type GalleryAttachment } from "./attachments/AttachmentGallery";
import { attachmentPreviewKind, storedGalleryPicture } from "./attachments/attachmentPreview";

interface LiveDocumentState {
  id: number;
  state: string | null;
  chatbot_attachment: boolean | null;
}

/**
 * The linked rows carry the document state as of the moment the issue was
 * loaded, so a file attached mid-pipeline read "Processing" for as long as
 * the panel stayed open. Re-read the live states here, and keep re-reading
 * while any of them is still in flight.
 */
function useLiveDocumentStates(ids: number[]): Map<number, LiveDocumentState> {
  const key = [...ids].sort((a, b) => a - b).join(",");
  const query = useQuery({
    queryKey: ["issue-linked-document-states", key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("documents")
        .select("id, state, chatbot_attachment")
        .in("id", ids);
      if (error) throw new Error(error.message);
      return (data ?? []) as LiveDocumentState[];
    },
    enabled: ids.length > 0,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((d) => isDocumentStillProcessing(d.state, d.chatbot_attachment))
        ? 15_000
        : false,
  });
  return new Map((query.data ?? []).map((d) => [d.id, d]));
}

const COLLAPSED_STORAGE_KEY = "pm-issue-documents-collapsed";

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Whether the band is folded. One preference for every issue, kept across
 * reloads: someone who folds the pictures away to read the comments wants
 * them folded on the next issue too.
 */
function useCollapsedPreference(): [boolean, (collapsed: boolean) => void] {
  const [collapsed, setCollapsedState] = useState(readCollapsed);
  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    try {
      window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
    } catch {
      // Storage unavailable (private mode, quota): the fold still works for
      // this session, it just is not remembered.
    }
  }, []);
  return [collapsed, setCollapsed];
}

interface IssueDocumentsSectionProps {
  issueId: number;
  buildingId: number;
  buildingProjectId: number;
  linkedDocuments: LinkedDocument[];
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  onRemoveDocument: (documentId: number) => void;
  onAttachmentsChanged: () => void;
  /** Read the links without being able to add or remove — snapshot mode. */
  readOnly?: boolean;
}

/**
 * IssueDocumentsSection — the linked-documents band of the expanded issue
 * view (WHA-3432). A compact header (label + count + single attach
 * dropdown) over the attachments: images and PDFs as a gallery of the
 * pictures themselves, every other file (and any whose preview cannot be
 * drawn) as a row beneath. Clicking the label folds the attachments away;
 * the attach menu stays. Owns the "Add from Franklin" picker dialog.
 */
export function IssueDocumentsSection({
  issueId,
  buildingId,
  buildingProjectId,
  linkedDocuments,
  openViewer,
  onRemoveDocument,
  onAttachmentsChanged,
  readOnly = false,
}: IssueDocumentsSectionProps) {
  const { t } = useLanguage();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [collapsed, setCollapsed] = useCollapsedPreference();
  const contentId = `issue-${issueId}-documents`;
  // A file attached while folded would land out of sight — unfold to show it.
  const handleAttachmentsChanged = useCallback(() => {
    setCollapsed(false);
    onAttachmentsChanged();
  }, [onAttachmentsChanged, setCollapsed]);
  // One predicate, one action: whatever `canOpen` says is clickable is exactly
  // what `open` acts on, so a DWG can never render as a button that no-ops.
  const { canOpen, open } = useOpenLinkedDoc(buildingId, openViewer);
  const liveStates = useLiveDocumentStates(linkedDocuments.map((d) => d.id));
  // Files whose preview could not be drawn (an unsupported variant, a broken
  // file) drop back to a plain row rather than leaving an empty tile.
  const [failedPreviewKeys, setFailedPreviewKeys] = useState<ReadonlySet<string>>(new Set());
  const handlePreviewFailed = useCallback((key: string) => {
    setFailedPreviewKeys((current) => (current.has(key) ? current : new Set(current).add(key)));
  }, []);

  const processingLabelFor = (doc: LinkedDocument): string | null => {
    const live = liveStates.get(doc.id);
    // One definition of "still processing" for the whole app — the local
    // two-value list here missed ERROR / UNZIPPED / IFC_PROCESSED. A chat
    // attachment parks at EXTRACTED for good, so it is done once readable.
    const processing = isDocumentStillProcessing(
      live ? live.state : doc.state,
      live ? live.chatbot_attachment : doc.chatbot_attachment
    );
    return processing ? t.pm.issues.detail.processingShort : null;
  };

  const removeButton = (doc: LinkedDocument, className: string): ReactNode =>
    readOnly ? undefined : (
      <button
        type="button"
        onClick={() => onRemoveDocument(doc.id)}
        aria-label={t.pm.issues.detail.removeDocument}
        className={className}
      >
        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
      </button>
    );

  const galleryAttachments: GalleryAttachment[] = [];
  const rowDocuments: LinkedDocument[] = [];
  for (const doc of linkedDocuments) {
    const kind = attachmentPreviewKind(doc);
    const picture = storedGalleryPicture(doc);
    if (kind && !failedPreviewKeys.has(picture.key)) {
      galleryAttachments.push({
        ...picture,
        kind,
        onOpen: canOpen(doc) ? () => open(doc) : undefined,
        processingLabel: processingLabelFor(doc),
        action: removeButton(
          doc,
          "flex h-5 w-5 items-center justify-center rounded bg-background/90 shadow-sm"
        ),
      });
    } else {
      rowDocuments.push(doc);
    }
  }

  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between gap-3">
        <PanelSectionLabel>
          {linkedDocuments.length > 0 ? (
            // The chevron trails the count so the label keeps the same left
            // edge as every other section label in the panel.
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              aria-expanded={!collapsed}
              aria-controls={contentId}
              className="inline-flex items-center gap-1 uppercase hover:text-foreground focus-visible:text-foreground focus-visible:underline focus-visible:outline-none"
            >
              {t.pm.issues.detail.documents}
              <span> · {linkedDocuments.length}</span>
              <ChevronRight
                aria-hidden
                className={cn("h-3 w-3 transition-transform", !collapsed && "rotate-90")}
              />
            </button>
          ) : (
            t.pm.issues.detail.documents
          )}
        </PanelSectionLabel>
        {!readOnly && (
          <IssueAttachMenu
            issueId={issueId}
            buildingId={String(buildingId)}
            buildingProjectId={buildingProjectId}
            onAddFromFranklin={() => setIsPickerOpen(true)}
            onUploaded={handleAttachmentsChanged}
          />
        )}
      </div>

      {/* Unmounted when folded, so no thumbnail is fetched or drawn. */}
      {!collapsed && (
        <div id={contentId}>
          {galleryAttachments.length > 0 && (
            <AttachmentGallery
              attachments={galleryAttachments}
              buildingId={buildingId}
              onPreviewFailed={handlePreviewFailed}
              showAllLabel={t.pm.issues.detail.showAllAttachments}
              showFewerLabel={t.pm.issues.detail.showFewerAttachments}
            />
          )}

          {rowDocuments.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {rowDocuments.map((doc) => (
                <LinkedDocumentRow
                  key={doc.id}
                  filename={doc.filename}
                  buildingId={buildingId}
                  breadcrumbSegments={doc.breadcrumbSegments}
                  onOpen={canOpen(doc) ? () => open(doc) : undefined}
                  unavailableLabel={t.pm.issues.detail.previewUnavailable}
                  processingLabel={processingLabelFor(doc)}
                  action={removeButton(doc, "opacity-0 transition-opacity group-hover:opacity-100")}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      <IssueDocumentPicker
        open={isPickerOpen}
        onOpenChange={setIsPickerOpen}
        issueId={issueId}
        buildingId={String(buildingId)}
        projectId={String(buildingProjectId)}
        alreadyAttachedIds={linkedDocuments.map((d) => String(d.id))}
        onAttached={handleAttachmentsChanged}
      />
    </div>
  );
}
